import React, { useState, useRef, useEffect } from 'react';
import { Filter } from 'lucide-react';

/**
 * ColumnFilterDropdown — filtro tipo Excel embebido en el header de una columna (PT-#32).
 *
 * Ícono de embudo en el header que abre un popover con checkboxes de los valores
 * distintos de la columna. Filtra la tabla IN-PLACE (client-side), combinable con el
 * orden server-side (solo acota las filas visibles, no reordena).
 *
 * Reusable por diseño: NO conoce el dominio de la columna. El host pasa `options`
 * (valores distintos + label + count opcional) y controla la selección vía
 * `selected`/`onChange`. `selected` vacío = sin filtro (se muestran todas las filas).
 *
 * Convención de estado activo: hay filtro cuando `selected.length > 0` y menor al total
 * de opciones (si están todas seleccionadas equivale a "todas" → se trata como sin filtro).
 */

export interface ColumnFilterOption {
  value: string;
  label: string;
  count?: number;
}

export interface ColumnFilterDropdownProps {
  /** Texto del header (ej. "Estado"). */
  label: string;
  options: ColumnFilterOption[];
  /** Valores seleccionados; vacío = todas las filas visibles. */
  selected: string[];
  onChange: (selected: string[]) => void;
}

const ColumnFilterDropdown: React.FC<ColumnFilterDropdownProps> = ({
  label,
  options,
  selected,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Filtro activo = hay selección parcial (ni vacía ni "todas").
  const isActive = selected.length > 0 && selected.length < options.length;

  const toggleValue = (value: string, checked: boolean) => {
    if (checked) {
      if (!selected.includes(value)) onChange([...selected, value]);
    } else {
      onChange(selected.filter(v => v !== value));
    }
  };

  const allChecked = options.length > 0 && selected.length === options.length;
  const toggleAll = () => {
    // "Todos": si están todas (o ninguna con filtro activo), limpiar → sin filtro.
    if (allChecked || selected.length === 0) onChange(options.map(o => o.value));
    else onChange([]);
  };

  return (
    <div ref={rootRef} className="relative inline-flex items-center gap-1">
      <span>{label}</span>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`p-0.5 rounded transition-colors ${
          isActive ? 'text-blue-600 bg-blue-100' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
        }`}
        title={isActive ? `Filtrando ${label}` : `Filtrar ${label}`}
        aria-label={`Filtrar ${label}`}
        aria-expanded={open}
      >
        <Filter className="w-3 h-3" fill={isActive ? 'currentColor' : 'none'} />
      </button>

      {open && (
        <div className="absolute z-20 top-full left-0 mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg text-xs font-normal normal-case text-gray-700">
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
            <label className="flex items-center gap-2 cursor-pointer font-medium">
              <input
                type="checkbox"
                checked={allChecked}
                ref={el => { if (el) el.indeterminate = isActive; }}
                onChange={toggleAll}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>Todos</span>
            </label>
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-blue-600 hover:text-blue-800"
              >
                Limpiar
              </button>
            )}
          </div>
          <div className="max-h-52 overflow-y-auto py-1">
            {options.map(opt => {
              const checked = selected.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={e => toggleValue(opt.value, e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="flex-1 truncate">{opt.label}</span>
                  {opt.count != null && (
                    <span className="text-gray-400 tabular-nums">{opt.count}</span>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnFilterDropdown;
